/**
 * Fixture Mission Config
 *
 * GW-5445 (P1-10): Stand-in for the real AppConfig response that gw-missions
 * will publish in Phase 2. Used by `missionConfigSlice` when
 * `runtimeConfig.missionsFixtureMode === true`.
 */

import type { MissionConfigPayload } from '@/types/missionConfig.types';

export const fixtureMissionConfig: MissionConfigPayload = {
  schemaVersion: '2.0',
  environment: 'dev',
  publishedAt: '2026-04-29T18:00:00Z',
  canonicalSteps: [],
  canonicalLoops: [],
  missions: [
    {
      missionId: 'MISSION-02',
      shortName: 'target_development',
      displayName: 'Target Development',
      description:
        'Build an intelligence portrait of a person, organization, or entity using open-source tradecraft.',
      tokenEstimate: { min: 120, max: 280 },
      icon: 'MISSION-02-target_development.svg',
      iconUrl: '/mission-icons/OSINT-MISSION-02.svg',
      enabled: true,
      promptPill: {
        prompt: 'develop a target profile for ',
        label: 'Target Development',
        tokensLow: 120,
        tokensHigh: 280,
        template: 'Develop a target profile on {subject} at {location}',
      },
      canonicalSteps: [
        {
          id: 'tgt-dir',
          name: 'Direction',
          short_name: 'direction',
          phase: 'direction',
          description: 'Frame the target question and define intelligence requirements.',
          tags: ['scoping', 'requirements'],
          inputs: ['requester_prompt'],
          outputs: ['mission_brief'],
          quality_gates: ['requirements_confirmed'],
        },
        {
          id: 'tgt-col',
          name: 'Collection',
          short_name: 'collection',
          phase: 'collection',
          description:
            'Gather open-source data on the target across public registries and web sources.',
          tags: ['osint', 'web'],
          inputs: ['mission_brief'],
          outputs: ['raw_findings'],
          quality_gates: ['source_coverage'],
        },
        {
          id: 'tgt-ana',
          name: 'Analysis',
          short_name: 'analysis',
          phase: 'analysis',
          description: 'Correlate findings and assess reliability to build the target portrait.',
          tags: ['analysis', 'correlation'],
          inputs: ['raw_findings'],
          outputs: ['assessed_entities'],
          quality_gates: ['analyst_review'],
        },
        {
          id: 'tgt-prd',
          name: 'Production',
          short_name: 'production',
          phase: 'production',
          description: 'Package the target profile into a concise intelligence product.',
          tags: ['writeup', 'product'],
          inputs: ['assessed_entities'],
          outputs: ['target_profile'],
          quality_gates: ['editor_approval'],
        },
      ],
    },
    {
      missionId: 'MISSION-11',
      shortName: 'due_diligence',
      displayName: 'Due Diligence',
      description:
        'Vet an organization, counterparty, or asset for reputational, regulatory, and financial risk.',
      tokenEstimate: { min: 180, max: 360 },
      icon: 'MISSION-11-due_diligence.svg',
      iconUrl: '/mission-icons/OSINT-MISSION-11.svg',
      enabled: true,
      promptPill: {
        prompt: 'conduct enhanced due diligence on ',
        label: 'Due Diligence',
        tokensLow: 400,
        tokensHigh: 600,
        template: 'Conduct due diligence on {subject} a {description} at/in {location}',
      },
      canonicalSteps: [
        {
          id: 'dd-dir',
          name: 'Direction',
          short_name: 'direction',
          phase: 'direction',
          description: 'Define diligence scope, risk appetite, and jurisdictions in play.',
          tags: ['scoping', 'risk'],
          inputs: ['requester_prompt'],
          outputs: ['diligence_plan'],
          quality_gates: ['scope_confirmed'],
        },
        {
          id: 'dd-col',
          name: 'Collection',
          short_name: 'collection',
          phase: 'collection',
          description:
            'Collect corporate records, sanctions data, adverse media, and beneficial ownership.',
          tags: ['corporate', 'sanctions', 'adverse-media'],
          inputs: ['diligence_plan'],
          outputs: ['raw_findings'],
          quality_gates: ['watchlist_coverage'],
        },
        {
          id: 'dd-ana',
          name: 'Analysis',
          short_name: 'analysis',
          phase: 'analysis',
          description: 'Assess reputational, regulatory, and financial risk indicators.',
          tags: ['risk-assessment'],
          inputs: ['raw_findings'],
          outputs: ['risk_findings'],
          quality_gates: ['analyst_review'],
        },
        {
          id: 'dd-prd',
          name: 'Production',
          short_name: 'production',
          phase: 'production',
          description: 'Produce the due-diligence report with findings and recommended actions.',
          tags: ['writeup', 'product'],
          inputs: ['risk_findings'],
          outputs: ['diligence_report'],
          quality_gates: ['editor_approval'],
        },
        {
          id: 'dd-dis',
          name: 'Dissemination',
          short_name: 'dissemination',
          phase: 'dissemination',
          description: 'Deliver the diligence report to the requester via the chosen channel.',
          tags: ['delivery'],
          inputs: ['diligence_report'],
          outputs: ['delivered_report'],
          quality_gates: ['delivery_confirmed'],
        },
      ],
    },
  ],
};
